
import React from 'react';
import { Badge as BadgeType } from '@/utils/gamification';
import { Card, CardContent } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Trophy, Lock, Sparkles } from 'lucide-react';

interface BadgeCardProps {
  badge: BadgeType;
  onClick?: (badge: BadgeType) => void;
  size?: 'sm' | 'md' | 'lg';
  showProgress?: boolean;
}

const BadgeCard: React.FC<BadgeCardProps> = ({ 
  badge, 
  onClick,
  size = 'md',
  showProgress = true
}) => {
  const handleClick = () => {
    if (onClick) {
      onClick(badge);
    }
  };
  
  const progress = Math.min(Math.round((badge.requiredProgress / badge.maxProgress) * 100), 100);
  
  const sizeClasses = {
    sm: 'w-20 h-20 text-lg',
    md: 'w-24 h-24 text-xl',
    lg: 'w-28 h-28 text-2xl'
  };
  
  return (
    <div className="flex flex-col items-center" onClick={handleClick}>
      <div 
        className={`relative ${badge.unlocked ? 'hover-scale cursor-pointer' : 'opacity-70'}`}
      >
        <div 
          className={`${sizeClasses[size]} rounded-full flex items-center justify-center mb-2 relative overflow-hidden`}
          style={{ 
            background: badge.unlocked ? badge.gradient || badge.backgroundColor : '#F1F0FB',
            boxShadow: badge.unlocked ? '0 8px 16px rgba(0, 0, 0, 0.1)' : 'none',
            border: badge.unlocked ? (badge.border || 'none') : '1px dashed #D1D5DB'
          }}
        >
          {badge.unlocked && (
            <div className="absolute inset-0 opacity-10 animate-pulse-soft">
              <Sparkles className="w-full h-full text-white" />
            </div>
          )}
          <span className="text-4xl z-10">{badge.icon}</span>
          {!badge.unlocked && (
            <div className="absolute inset-0 bg-gray-100/80 backdrop-blur-sm flex items-center justify-center">
              <Lock size={size === 'sm' ? 18 : 24} className="text-gray-400" />
            </div>
          )}
        </div>
        {badge.unlocked && (
          <div className="absolute -top-1 -right-1">
            <div className="bg-white rounded-full p-1 shadow-sm">
              <Trophy size={size === 'sm' ? 14 : 16} className="text-hormony-coral" />
            </div>
          </div>
        )}
      </div>
      <div className="text-center">
        <h4 className={`font-medium text-hormony-charcoal ${size === 'sm' ? 'text-xs' : 'text-sm'}`}>
          {badge.name}
        </h4>
        {showProgress && !badge.unlocked && (
          <div className="mt-1">
            <div className="flex justify-between items-center mb-1">
              <span className="text-xs text-hormony-charcoal/60">
                {badge.requiredProgress}/{badge.maxProgress}
              </span>
            </div>
            <Progress value={progress} className="h-1.5 w-full max-w-24" />
          </div>
        )}
      </div>
    </div>
  );
};

export default BadgeCard;
